import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import {
    useLazySearchProductsByNameQuery,
    useLazySearchProductByIdQuery,
    useLazySearchByProductRowIdQuery,
    useLazySearchByProductCodeQuery,
} from 'features/products/redux/productApiSlice';
import { setProducts, setProductLoading, setProductError, setLastSearchParams, deleteProduct, clearSelectedProduct, setSelectedRowId } from 'features/products/redux/productSlice';
import { toast } from 'react-toastify';

export interface SearchField {
    id: number;
    name: string;
    value: string;
}

export const PRODUCT_SEARCH_FIELDS: SearchField[] = [
    {
        id: 1,
        name: 'Product Name',
        value: 'productName',
    },
    {
        id: 2,
        name: 'Product ID',
        value: 'productId',
    },
    {
        id: 3,
        name: 'Product Code',
        value: 'productCode',
    },
    {
        id: 4,
        name: 'Product Row ID',
        value: 'productRowId',
    },
];

export const useProductSearch = () => {
    const dispatch = useDispatch();
    const { orgId, userId } = useSelector((state: RootState) => state.auth);
    const lastSearchParams = useSelector((state: RootState) => state.products.lastSearchParams);

    // UI State
    const [searchValue, setSearchValue] = useState('');
    const [selectedField, setSelectedField] = useState(PRODUCT_SEARCH_FIELDS[0].name);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // API hooks
    const [triggerByName] = useLazySearchProductsByNameQuery();
    const [triggerById] = useLazySearchProductByIdQuery();
    const [triggerByRowId] = useLazySearchByProductRowIdQuery();
    const [triggerByProductCode] = useLazySearchByProductCodeQuery();

    // Handlers
    const toggleDropdown = () => setDropdownOpen((prev) => !prev);

    const handleFieldChange = (fieldName: string) => {
        setSelectedField(fieldName);
        setDropdownOpen(false);
    };

    // Core search execution function
    const executeSearch = async (searchParams: {
        searchValue: string;
        selectedField: string;
        orgId: number;
        userId: number;
    }) => {
        const { searchValue: searchVal, selectedField: field, orgId: orgIdParam, userId: userIdParam } = searchParams;
        
        try {
            dispatch(setProductLoading(true));
            
            let result;
            
            switch (field) {
                case 'Product Name':
                    result = await triggerByName({
                        orgId: Number(orgIdParam),
                        userId: Number(userIdParam),
                        searchText: searchVal,
                    });
                    break;
                    
                case 'Product ID':
                    if (isNaN(Number(searchVal))) {
                        toast.error('Product ID must be a number');
                        return;
                    }
                    result = await triggerById({
                        orgId: Number(orgIdParam),
                        userId: Number(userIdParam),
                        productId: Number(searchVal),
                    });
                    break;
                    
                case 'Product Code':
                    result = await triggerByProductCode({
                        productCode: searchVal,
                    });
                    break;
                    
                case 'Product Row ID':
                    if (isNaN(Number(searchVal))) {
                        toast.error('Product Row ID must be a number');
                        return;
                    }
                    result = await triggerByRowId({
                        orgId: Number(orgIdParam),
                        userId: Number(userIdParam),
                        productRowId: Number(searchVal),
                    });
                    break;
                    
                default:
                    toast.error('Invalid search field selected');
                    return;
            }
            
            if (result?.data?.data) {
                dispatch(setProducts(result.data.data));
                toast.success(`Found ${result.data.data.length} product(s)`);
                
                // Store the search parameters for future refresh
                dispatch(setLastSearchParams({
                    searchValue: searchVal,
                    selectedField: field,
                    orgId: Number(orgIdParam),
                    userId: Number(userIdParam),
                }));
            } else {
                toast.info('No products found');
                dispatch(setProducts([]));
            }
            
        } catch (error) {
            console.error('Error searching products:', error);
            toast.error('Failed to search products. Please try again.');
            dispatch(setProductError('Failed to search products'));
        } finally {
            dispatch(setProductLoading(false));
        }
    };

    const handleSearch = async () => {
        if (!searchValue.trim()) {
            toast.warning('Please enter a search value');
            return;
        }

        await executeSearch({
            searchValue,
            selectedField,
            orgId: Number(orgId),
            userId: Number(userId),
        });
    };

    // Function to refresh the last search
    const refreshLastSearch = async () => {
        if (!lastSearchParams) {
            console.warn('No previous search to refresh');
            return;
        }

        await executeSearch(lastSearchParams);
    };

    // Function to refresh and force auto-selection of first item
    const refreshLastSearchAndAutoSelect = async () => {
        if (!lastSearchParams) {
            console.warn('No previous search to refresh');
            return;
        }

        // Clear current selection first to ensure auto-selection triggers
        dispatch(setProducts([]));
        dispatch(clearSelectedProduct());
        dispatch(setSelectedRowId(null));
        
        // Small delay to ensure state is updated
        await new Promise(resolve => setTimeout(resolve, 50));
        
        await executeSearch(lastSearchParams);
    };

    const handleClear = () => {
        setSearchValue('');
        setSelectedField(PRODUCT_SEARCH_FIELDS[0].name);
        dispatch(setProducts([]));
    };

    // Function to remove a deleted product from the current results
    const removeDeletedProduct = (productRowId: number) => {
        dispatch(deleteProduct(productRowId));
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    return {
        // State
        searchValue,
        selectedField,
        dropdownOpen,
        searchFields: PRODUCT_SEARCH_FIELDS,
        
        // Handlers
        setSearchValue,
        toggleDropdown,
        handleFieldChange,
        handleSearch,
        handleClear,
        handleKeyPress,
        
        // New functions for refresh and deletion
        refreshLastSearch,
        refreshLastSearchAndAutoSelect,
        removeDeletedProduct,
    };
};
